import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Select, Input, Checkbox, Modal } from 'antd';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeStats, NdoeStatCard, NdoeActionButton,
  NdoeLoading, NdoeError, NdoeEmpty, NdoeRecherche, filtreTexte, compterActifs,
} from '../../components/ndoe';
import './commandesDormantes.css';

/*
  Commandes dormantes — le ménage qui rend des paires à la vente.

  POURQUOI CET ÉCRAN (mesuré en prod le 30/07)
  222 commandes étaient affectées à un livreur sans jamais avoir été prises en
  charge, certaines depuis plus de deux ans. Ce n'est pas du désordre : les
  lignes restées en AFFECTEE RÉSERVENT le stock. 283 paires physiquement en
  boutique apparaissaient indisponibles. Le soft-delete existait déjà mais
  n'avait servi qu'une fois — parce que rien ne montrait le problème.

  PARTI PRIS : on trie par IMPACT (paires immobilisées d'abord), pas par date.
  Un ménage utile, pas fastidieux.

  ON PROPOSE, ON N'EXÉCUTE PAS : aucune purge automatique. Une commande dormante
  peut être une cliente qu'on relance.
*/

const SEUILS = [
  { value: 7, label: '+ de 7 jours' },
  { value: 30, label: '+ de 30 jours' },
  { value: 60, label: '+ de 60 jours' },
  { value: 180, label: '+ de 6 mois' },
];

const CommandesDormantes = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [jours, setJours] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [q, setQ] = useState('');
  const [choisies, setChoisies] = useState(() => new Set());
  const [confirme, setConfirme] = useState(false);
  const [motif, setMotif] = useState(undefined);
  const [commentaire, setCommentaire] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true); setErreur('');
    try {
      const { data: r } = await axios.get(`${DOMAIN}/api/commande/dormantes`, { params: { jours, limite: 300 } });
      setData(r);
      setChoisies(new Set()); // le seuil change → la sélection n'a plus de sens
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Impossible de charger les commandes dormantes.');
    } finally {
      setLoading(false);
    }
  }, [DOMAIN, jours]);

  useEffect(() => { charger(); }, [charger]);

  const commandes = useMemo(
    () => (data?.commandes || []).filter(filtreTexte(q, [
      'cliente', 'telephone',
      (c) => c.id_commande,
      (c) => (c.produits || []).join(' '),
    ])),
    [data, q],
  );

  const basculer = (id) => setChoisies((prev) => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const toutBasculer = () => setChoisies((prev) => (
    prev.size === commandes.length ? new Set() : new Set(commandes.map((c) => c.id_commande))
  ));

  const selection = commandes.filter((c) => choisies.has(c.id_commande));
  const pairesRendues = selection.reduce((s, c) => s + (c.paires_bloquees || 0), 0);

  const annuler = async () => {
    if (!motif) { toast.error('Choisissez un motif.'); return; }
    setEnvoi(true);
    try {
      const { data: r } = await axios.put(`${DOMAIN}/api/commande/annuler-lot`, {
        ids: selection.map((c) => c.id_commande), motif, commentaire,
      });
      // Le serveur repasse les garde-fous ligne par ligne : un refus n'empêche
      // pas les autres. On rend compte des deux.
      if (r.annulees) {
        toast.success(`${r.annulees} commande${r.annulees > 1 ? 's annulées' : ' annulée'}${r.paires_liberees ? ` — ${r.paires_liberees} paire${r.paires_liberees > 1 ? 's rendues' : ' rendue'} au stock` : ''}.`);
      }
      if (r.refusees) {
        toast.warning(`${r.refusees} refusée${r.refusees > 1 ? 's' : ''} : ${r.refus.slice(0, 2).map((x) => `#${x.id_commande} ${x.raison}`).join(' · ')}`);
      }
      setConfirme(false); setMotif(undefined); setCommentaire('');
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Annulation impossible.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <NdoePage>
      <NdoePageHeader
        title="Commandes dormantes"
        subtitle="Affectées à un livreur mais jamais prises en charge — elles réservent du stock"
      />

      {loading ? <NdoeLoading rows={6} />
        : erreur ? <NdoeError text={erreur} onRetry={charger} />
          : (
            <>
              <NdoeStats>
                <NdoeStatCard label="Commandes dormantes" value={data?.total || 0} />
                <NdoeStatCard label="Paires immobilisées" value={data?.paires_bloquees || 0} gold />
                <NdoeStatCard label="Sélectionnées" value={selection.length} />
              </NdoeStats>

              <NdoeRecherche
                q={q}
                onQ={setQ}
                placeholder="Cliente, téléphone, n° de commande, modèle…"
                facettes={[{
                  cle: 'seuil',
                  placeholder: 'Ancienneté',
                  options: SEUILS.map((s) => ({ value: String(s.value), label: s.label })),
                  valeur: String(jours),
                  onChange: (v) => setJours(Number(v) || 30),
                  largeur: 170,
                }]}
                actifs={compterActifs(q)}
                onEffacer={() => setQ('')}
                resume={`${commandes.length} sur ${data?.total || 0} commande${(data?.total || 0) > 1 ? 's' : ''}`}
              />

              {!commandes.length ? (
                <NdoeEmpty icon="✅" title="Rien à ranger" text="Aucune commande dormante sur cette période." />
              ) : (
                <>
                  <div className="cdo-barre">
                    <Checkbox
                      checked={selection.length === commandes.length && commandes.length > 0}
                      indeterminate={selection.length > 0 && selection.length < commandes.length}
                      onChange={toutBasculer}
                    >
                      Tout sélectionner
                    </Checkbox>
                    <NdoeActionButton
                      variant="danger"
                      disabled={!selection.length}
                      onClick={() => setConfirme(true)}
                    >
                      Annuler la sélection ({selection.length})
                    </NdoeActionButton>
                  </div>

                  <ul className="cdo-liste">
                    {commandes.map((c) => (
                      <li key={c.id_commande} className={`cdo-item${choisies.has(c.id_commande) ? ' is-choisie' : ''}`}>
                        <Checkbox
                          checked={choisies.has(c.id_commande)}
                          onChange={() => basculer(c.id_commande)}
                        />
                        <div className="cdo-item__corps">
                          <div className="cdo-item__tete">
                            <b>#{c.id_commande}</b>
                            <span className="cdo-item__cliente">{c.cliente || 'Cliente inconnue'}</span>
                            {c.paires_bloquees > 0 && (
                              <span className="cdo-item__paires">
                                {c.paires_bloquees} paire{c.paires_bloquees > 1 ? 's' : ''} immobilisée{c.paires_bloquees > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <small>
                            Dort depuis {c.age_jours} jours
                            {c.produits?.length ? ` · ${c.produits.join(' · ')}` : ''}
                          </small>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}

      <Modal
        title="Annuler ces commandes"
        open={confirme}
        onCancel={() => setConfirme(false)}
        okText={envoi ? 'Annulation…' : 'Confirmer'}
        cancelText="Retour"
        okButtonProps={{ danger: true, loading: envoi, disabled: !motif }}
        onOk={annuler}
        destroyOnClose
      >
        <p className="cdo-resume">
          <b>{selection.length} commande{selection.length > 1 ? 's' : ''}</b>
          {pairesRendues > 0 && <> · <b>{pairesRendues} paire{pairesRendues > 1 ? 's' : ''}</b> rendue{pairesRendues > 1 ? 's' : ''} au stock</>}
        </p>
        <label className="cdo-champ">
          <span>Motif <b>*</b></span>
          <Select
            placeholder="Pourquoi ces commandes sont-elles annulées ?"
            value={motif}
            onChange={setMotif}
            options={(data?.motifs || []).map((m) => ({ value: m.code, label: m.libelle }))}
            style={{ width: '100%' }}
          />
        </label>
        <label className="cdo-champ">
          <span>Précision (facultatif)</span>
          <Input.TextArea
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            rows={2}
            maxLength={160}
            placeholder="Contexte utile pour plus tard"
          />
        </label>
        <p className="cdo-aide">
          Les commandes restent consultables et <b>restaurables</b> depuis le Journal des
          suppressions. Celles qui portent une vente, un paiement ou une paire déjà partie en
          livraison seront <b>refusées</b> — le lot n'est pas une dérogation.
        </p>
      </Modal>
    </NdoePage>
  );
};

export default CommandesDormantes;
